import { createBrowserRouter } from "react-router-dom"
import AuthenticatedRoute from "../components/AuthenticatedRoute"
import LayoutComponent from "../components/LayoutComponent"
import ErrorScreen from "../core-components/theme/components/ErrorScreen"
import withLazyLoading from "../hoc/withLazyLoading"
import { validatePermission, createCrumbHandle } from "../utilities/auth"
import dashboardRoutes from "./modulesRoutes/dashboardRoutes"
import formulaRoutes from "./modulesRoutes/formulaRoutes"
import inventoryRoutes from "./modulesRoutes/inventoryRoutes"
import miscRoutes from "./modulesRoutes/miscRoutes"
import plantRoutes from "./modulesRoutes/plantRoutes"
import taskRoutes from "./modulesRoutes/taskRoutes"
import userRoutes from "./modulesRoutes/userRoutes"
import visualizationRoutes from "./modulesRoutes/visualizationRoutes"
import workspaceRoutes from "./modulesRoutes/workspaceRoutes"
import manualInputRoutes from "./modulesRoutes/manualInputRoutes"
import dataInputRoutes from "./modulesRoutes/dataInputRoutes"
import Notifications from "../components/Notifications/pages/Notifications"
import { QueryClient } from "@tanstack/react-query"
import fileManagementRoute from "./modulesRoutes/fileManagementRoute"
import batchConfigRoute from "./modulesRoutes/batchConfigRoute"
import setPointConfigurationRoute from "./modulesRoutes/setPointConfigurationRoute"
import publicRoutes from "./modulesRoutes/publicRoutes"
import DataLoggerList from "../components/DataLogger/components/DataLoggerList"
import withPostHogTracker from "../composeRouter"

const LoginV2 = withLazyLoading(() => import("../pages/LoginV2"))
const Home = withLazyLoading(() => import("../components/Home"))
const Page404 = withLazyLoading(() => import("../pages/Page404"))
const CompanyListing = withLazyLoading(() => import("../pages/company/companyListing"));
const CompanyDetailsListing = withLazyLoading(() => import("../pages/company/companyDetailsListing"))
const SensorListing = withLazyLoading(() => import("../pages/sensor/sensorListing"))
const InsightsTemplateList = withLazyLoading(() => import("../components/Insights/components/InsightsTemplateList"));
const EventTemplateList = withLazyLoading(() => import("../components/Events/components/EventTemplateList"));
const CreateInsightsTemplatePage = withLazyLoading(() => import("../components/Insights/components/AddInsightsTemplate"))
const InsightsListView = withLazyLoading(() => import("../components/Insights/components/InsightsListView"))
const EventList = withLazyLoading(() => import("../components/Events/components/EventList"))
const AuthorizationFailed = withLazyLoading(
    () => import('../core-components/theme/components/DialogBox/AuthorizationFailed')
);
const ForgotPasswordV2 = withLazyLoading(
    () => import('../pages/ForgotPasswordV2')
);
const ResetPassword = withLazyLoading(
    () => import('../pages/ResetPassword')
);
const EmailPage = withLazyLoading(() => import('../pages/EmailPage'));
const DashboardA4PrintPage = withLazyLoading(() => import('../pages/dashboardA4PrintPage'));

const router = (queryClient: QueryClient) => createBrowserRouter([
  {
    path: "/Login",
    element: <LoginV2 />,
  },
  ...publicRoutes,
  {
    path: "/ForgotPasswordV2",
    element: withPostHogTracker(<ForgotPasswordV2 />),
    handle: createCrumbHandle("Forgot Password"),
  },
  {
    path: "/resetPassword",
    element: withPostHogTracker(<ResetPassword />),
    loader: validatePermission("ForgetPassword_Usage_WE"),
    handle: createCrumbHandle("Reset Password"),
  },
  {
    path: "/emailPage/:plantId/:pageId/:days/:startDateUrl/:endDateUrl/:granularity",
    element: withPostHogTracker(<EmailPage />),
  },
  {
    path: "/dashboardA4PrintPage/:plantId/:pageId/:days/:startDateUrl/:endDateUrl/:granularity",
    element: withPostHogTracker(<DashboardA4PrintPage />),
  },
  {
    element: <AuthenticatedRoute />,
    errorElement: <ErrorScreen />,
    children: [
      {
        path: "/",
        element: <LayoutComponent />,
        errorElement: <ErrorScreen />,
        children: [
          {
            index: true,
            element: withPostHogTracker(<Home />),
          },
          {
            path: "/company-list",
            handle: createCrumbHandle("Companies"),
            children: [
              {
                index: true,
                element: withPostHogTracker(<CompanyListing />),
                loader: validatePermission("Company_Configuration_WE"),
              },
              {
                path: "company-details/:companyid?",
                element: withPostHogTracker(<CompanyDetailsListing />),
                loader: validatePermission("Company_Configuration_WE"),
                handle: createCrumbHandle("Company Details"),
              },
            ],
          },
          {
            path: "/sensor-list",
            element: withPostHogTracker(<SensorListing />),
            loader: validatePermission("Sensor_Configuration_WE"),
            handle: createCrumbHandle("Sensors"),
          },
          {
            path: "/insightsTemplates",
            handle: createCrumbHandle("Insights Templates"),
            children: [
              {
                index: true,
                element: withPostHogTracker(<InsightsTemplateList />),
                loader: validatePermission("InsightTemplate_View_R"),
              },
              {
                path: "insightsTemplate/:insightsTemplateId?",
                element: withPostHogTracker(<CreateInsightsTemplatePage />),
                loader: validatePermission("InsightTemplate_Manage_A"),
                handle: createCrumbHandle("Create Insights Template"),
              },
            ],
          },
          {
            path: "/data-logger",
            element: withPostHogTracker(<DataLoggerList />),
            loader: validatePermission("InsightTemplate_View_R"),
            handle: createCrumbHandle("Data Logger"),
          },
          {
            path: "/insightList",
            element: withPostHogTracker(<InsightsListView />),
            loader: validatePermission("Insights_View_R"),
            handle: createCrumbHandle("Insights"),
          },
          {
            path: "/eventTemplates",
            element: withPostHogTracker(<EventTemplateList />),
            loader: validatePermission("EventTemplate_View_R"),
            handle: createCrumbHandle("Event Templates"),
          },
          {
            path: "/events",
            element: withPostHogTracker(<EventList />),
            loader: validatePermission("Events_View_R"),
            handle: createCrumbHandle("Insights"),
          },
          {
            path: "/authorizationFailed",
            element: withPostHogTracker(<AuthorizationFailed />),
          },
          {
            path: "/notifications",
            element: withPostHogTracker(<Notifications />),
          },
          {
            path: "*",
            element: withPostHogTracker(<Page404 />),
          },
          // spread subroutes unchanged
          ...userRoutes(queryClient),
          ...dashboardRoutes,
          ...plantRoutes,
          ...taskRoutes,
          ...formulaRoutes,
          ...workspaceRoutes,
          ...visualizationRoutes(queryClient),
          ...inventoryRoutes,
          ...miscRoutes(queryClient),
          ...manualInputRoutes,
          ...dataInputRoutes,
          ...fileManagementRoute,
          ...batchConfigRoute,
          ...setPointConfigurationRoute,
        ],
      },
    ],
  },
]);


export default router;